<template>
  <div class="generate-password-wrapper">
    <gg-input-label v-if="$attrs['label']">
      {{ $attrs['label'] }}
    </gg-input-label>
    <v-text-field
        class="compact-password"
        v-bind="$attrs"
        :type="inputType"
        v-model="password"
        :label="''"
        :loading="loading && showStrength"
        dense
    >
      <template v-if="withGenerator" v-slot:append>
        <v-tooltip v-if="showAlertIcon" bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-icon
                :color="strengthColor"
                v-bind="attrs"
                v-on="on"
            >
              mdi-alert-outline
            </v-icon>
          </template>
          <span>Please, use a stronger password.</span>
        </v-tooltip>
        <v-btn
            icon
            :ripple="false"
            class="mt-0 mr-1"
            @click="changeInputType"
        >
          <v-icon color="#C2C2C2">{{ appendIcon }}</v-icon>
        </v-btn>
        <gg-btn
            tile
            depressed
            elevation="0"
            class="generate-btn"
            color="#ececec"
            @click="generatePassword"
        >
          Generate
        </gg-btn>
      </template>
      <template v-slot:progress v-if="cpanelApp">
        <v-progress-linear
            v-if="showStrength"
            :value="currentStrength"
            :color="strengthColor"
            absolute
            height="3"
        />
      </template>
    </v-text-field>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import amapi from '@/api/amapi'
import debounce from 'lodash.debounce'

export default {
  name: 'CompactPassword',
  model: {
    prop: 'value',
    event: 'input'
  },
  props: {
    value: {
      type: String,
      default: ''
    },
    name: {
      type: String,
      default: 'Password'
    },
    withGenerator: {
      type: Boolean,
      default: false
    },
    showStrength: {
      type: Boolean,
      default: false
    },
    charset: {
      type: String,
      default: 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMOPRSTUVWXYZ0123456789!@#$%^&*()_+~`|}{[]:;?><,./-='
    },
    mustIncludeChars: {
      type: String,
      default: ''
    },
    mustIncludeNumber: {
      type: Boolean,
      default: true
    },
    passwordLength: {
      type: Number,
      default: 12
    },
    cpanelApp: {
      type: [String, Boolean],
      required: false,
      default: false,
      validator (val) {
        // https://api.docs.cpanel.net/openapi/cpanel/operation/get_required_strength
        return [
          'cpaddons',
          'createacct',
          'ftp',
          'htaccess',
          'mysql',
          'passwd',
          'pop',
          'postgres',
          'sshkey',
          'webdisk',
          'virtual'
        ].includes(val) || val === false
      }
    }
  },
  data () {
    return {
      inputType: 'password',
      password: '',
      minimalStrength: 0,
      currentStrength: 0,
      loadingStrength: true
    }
  },
  computed: {
    ...mapGetters(['hosting']),
    appendIcon () {
      return this.inputType === 'password' ? 'mdi-eye' : 'mdi-eye-off'
    },
    strengthColor () {
      if (this.currentStrength < this.minimalStrength * 0.75) return 'error'
      if (this.currentStrength < this.minimalStrength) return 'warning'
      return 'primary'
    },
    loading () {
      return this.cpanelApp && this.minimalStrength > 0
    },
    showAlertIcon () {
      return !this.loadingStrength &&
        this.password.length > 0 &&
        this.minimalStrength > 0 &&
        this.currentStrength < this.minimalStrength
    },
  },
  methods: {
    ...mapActions({
      cpanel_uapi: 'cpanel_uapi',
      reportError: 'reportError'
    }),
    generatePassword () {
      let pwd = ''
      for (let i = 0, n = this.charset.length; i < this.passwordLength; ++i) {
        pwd += this.charset.charAt(Math.floor(Math.random() * n))
      }
      // must include at least one character from mustIncludeChars
      if (this.mustIncludeChars.length) {
        const pos = Math.floor(Math.random() * pwd.length - 2)
        const specialPos = Math.floor(Math.random() * this.mustIncludeChars.length)
        pwd = pwd.slice(0, pos) + this.mustIncludeChars.charAt(specialPos) + pwd.slice(pos + 1)
      }
      // must include at least one number
      if (this.mustIncludeNumber) {
        const test = /\d/
        if (!test.test(pwd)) {
          const numberChar = '0123456789'.charAt(Math.floor(Math.random() * 10))
          pwd = pwd.slice(0, pwd.length - 1) + numberChar
        }
      }
      this.password = pwd
    },
    changeInputType () {
      this.inputType = this.inputType === 'password' ? 'text' : 'password'
      this.$emit('input-type', this.inputType)
    },
    async getMinStrength () {
      const data = {
        uapi_module: 'PasswdStrength',
        uapi_function: 'get_required_strength',
        app: this.cpanelApp
      }
      try {
        const response = await this.cpanel_uapi(data)
        this.minimalStrength = response.payload?.data?.strength
      } catch (e) {
        this.reportError(e.resultmsg)
      }
    },
    checkStrength: debounce(async function () {
      if (this.password.length > 0) {
        this.loadingStrength = true
        try {
          const response = await amapi.makecall('cpanel_passwordstrength', {
            id: this.hosting.id,
            password: this.password
          })
          this.currentStrength = response.strength
        } catch (e) {
          this.reportError(e)
        }
        this.loadingStrength = false
      } else {
        this.currentStrength = 0
      }
    }, 800)
  },
  watch: {
    password (val) {
      if (this.cpanelApp) {
        this.checkStrength()
      }
      this.$emit('input', val)
    },
    value (val) {
      this.password = val
    }
  },
  mounted () {
    this.$nextTick(() => {
      this.password = this.value
    })
    if (this.cpanelApp) {
      this.getMinStrength()
    }
  },
}
</script>

<style>
.compact-password .v-input__append-inner {
  margin: 2px 2px 0 0 !important;
}

.compact-password .v-input__slot {
  padding-right: 1px !important;
}

.compact-password .v-text-field__details {
  padding-left: 0 !important;
}
</style>
